import * as React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva } from "class-variance-authority";
import { cn } from "@/shadcn/lib/utils";

import { Separator } from "@/shadcn/components/ui/separator";

// playerdex · registry v1 · shadcn@4.21.0

const ItemGroup = React.forwardRef(function ItemGroup(
  { className, ...props },
  ref
) {
  return (
    <div
      role="list"
      data-slot="item-group"
      className={cn(
        "gap-4 has-data-[size=sm]:gap-2.5 has-data-[size=xs]:gap-2 group/item-group flex w-full flex-col",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

const ItemSeparator = React.forwardRef(function ItemSeparator(
  { className, ...props },
  ref
) {
  return (
    <Separator
      data-slot="item-separator"
      orientation="horizontal"
      className={cn("my-2", className)}
      ref={ref}
      {...props}
    />
  );
});

const itemVariants = cva(
  "[a]:hover:bg-muted rounded-2xl border text-sm group/item flex w-full flex-wrap items-center transition-colors duration-100 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [a]:transition-colors",
  {
    variants: {
      variant: {
        default: "border-transparent",
        outline: "border-border",
        muted: "bg-muted/50 border-transparent",
      },
      size: {
        default: "gap-3.5 px-4 py-3.5",
        sm: "gap-3.5 px-3.5 py-3",
        xs: "gap-2.5 px-3 py-2.5 in-data-[slot=dropdown-menu-content]:p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Item = React.forwardRef(function Item(
  { className, variant = "default", size = "default", render, ...props },
  ref
) {
  return useRender({
    ref,
    defaultTagName: "div",
    props: mergeProps(
      {
        className: cn(itemVariants({ variant, size, className })),
      },
      props
    ),
    render,
    state: {
      slot: "item",
      variant,
      size,
    },
  });
});

const itemMediaVariants = cva(
  "gap-2 group-has-data-[slot=item-description]/item:translate-y-0.5 group-has-data-[slot=item-description]/item:self-start flex shrink-0 items-center justify-center [&_svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "[&_svg:not([class*='size-'])]:size-4",
        image:
          "size-10 overflow-hidden rounded-xl group-data-[size=sm]/item:size-8 group-data-[size=xs]/item:size-6 group-data-[size=xs]/item:rounded-lg [&_img]:size-full [&_img]:object-cover",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const ItemMedia = React.forwardRef(function ItemMedia(
  { className, variant = "default", ...props },
  ref
) {
  return (
    <div
      data-slot="item-media"
      data-variant={variant}
      className={cn(itemMediaVariants({ variant, className }))}
      ref={ref}
      {...props}
    />
  );
});

const ItemContent = React.forwardRef(function ItemContent(
  { className, ...props },
  ref
) {
  return (
    <div
      data-slot="item-content"
      className={cn(
        "gap-1 group-data-[size=xs]/item:gap-0.5 flex flex-1 flex-col [&+[data-slot=item-content]]:flex-none",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

const ItemTitle = React.forwardRef(function ItemTitle(
  { className, ...props },
  ref
) {
  return (
    <div
      data-slot="item-title"
      className={cn(
        "gap-2 text-sm leading-snug font-medium underline-offset-4 line-clamp-1 flex w-fit items-center",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

const ItemDescription = React.forwardRef(function ItemDescription(
  { className, ...props },
  ref
) {
  return (
    <p
      data-slot="item-description"
      className={cn(
        "text-muted-foreground text-left text-sm line-clamp-2 font-normal [&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

const ItemActions = React.forwardRef(function ItemActions(
  { className, ...props },
  ref
) {
  return (
    <div
      data-slot="item-actions"
      className={cn("gap-2 flex items-center", className)}
      ref={ref}
      {...props}
    />
  );
});

const ItemHeader = React.forwardRef(function ItemHeader(
  { className, ...props },
  ref
) {
  return (
    <div
      data-slot="item-header"
      className={cn(
        "gap-2 flex basis-full items-center justify-between",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

const ItemFooter = React.forwardRef(function ItemFooter(
  { className, ...props },
  ref
) {
  return (
    <div
      data-slot="item-footer"
      className={cn(
        "gap-2 flex basis-full items-center justify-between",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

export {
  Item,
  ItemMedia,
  ItemContent,
  ItemActions,
  ItemGroup,
  ItemSeparator,
  ItemTitle,
  ItemDescription,
  ItemHeader,
  ItemFooter,
};
